import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageSquare, Clock, AlertCircle } from 'lucide-react';

export default function HelpTicketsPanel({ tickets }) {
  const navigate = useNavigate();

  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / 3600000);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const needsAttention = tickets.filter(t => 
    t.status !== 'closed' && t.last_reply_by === 'user'
  ).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Help Tickets</h2>
        {needsAttention > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm font-semibold text-red-700">
              {needsAttention} ticket{needsAttention !== 1 ? 's' : ''} need{needsAttention === 1 ? 's' : ''} attention
            </span>
          </div>
        )}
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-600 mb-1">Total</p>
          <p className="text-3xl font-bold text-slate-900">{tickets.length}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-4">
          <p className="text-sm text-yellow-700 mb-1">Open</p>
          <p className="text-3xl font-bold text-yellow-700">
            {tickets.filter(t => t.status === 'open').length}
          </p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <p className="text-sm text-blue-700 mb-1">In Progress</p>
          <p className="text-3xl font-bold text-blue-700">
            {tickets.filter(t => t.status === 'in_progress').length}
          </p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
          <p className="text-sm text-green-700 mb-1">Resolved</p>
          <p className="text-3xl font-bold text-green-700">
            {tickets.filter(t => t.status === 'resolved').length}
          </p>
        </div>
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-600 mb-1">Closed</p>
          <p className="text-3xl font-bold text-slate-700">
            {tickets.filter(t => t.status === 'closed').length}
          </p>
        </div>
      </div>
      
      <div className="space-y-3">
        {tickets.map(ticket => {
          const needsAdminAttention = ticket.status !== 'closed' && ticket.last_reply_by === 'user';
          
          return (
            <div
              key={ticket.ticket_id}
              onClick={() => navigate(`/admin/tickets/${ticket.ticket_id}`)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                needsAdminAttention ? 'border-red-300 bg-red-50' :
                ticket.status === 'open' ? 'border-yellow-200 bg-yellow-50 hover:border-yellow-300' :
                ticket.status === 'in_progress' ? 'border-blue-200 bg-blue-50 hover:border-blue-300' :
                ticket.status === 'resolved' ? 'border-green-200 bg-green-50 hover:border-green-300' :
                'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900">{ticket.subject}</h3>
                    {needsAdminAttention && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                        NEEDS ATTENTION
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{ticket.user_name || ticket.user_email || 'Anonymous'}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                  ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                  ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {ticket.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Opened {getTimeSince(ticket.created_at)}
                </span>
                {ticket.last_reply_at && (
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    Last activity {getTimeSince(ticket.last_reply_at)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
